import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { api, formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

const STATUS_PL = { APPLIED: "ZGŁOSZONA", VERIFIED: "ZWERYFIKOWANA", ACTIVE: "AKTYWNA",
  SUSPENDED: "ZAWIESZONA", CLOSED: "ZAMKNIĘTA" };

export default function AdminEduPage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [o, i, r, x] = await Promise.all([
        api.get("/admin/edu/overview"),
        api.get("/admin/edu/institutions"),
        api.get("/admin/edu/reviews"),
        api.get("/admin/edu/invite-exceptions")]);
      setOverview(o.data); setInstitutions(i.data.results || []); setReviews(r.data.results || []);
      setExceptions(x.data.results || []);
    } catch (ex) { setErr(formatApiError(ex)); }
  }, []);

  useEffect(() => { if (user && user.role === "admin") load(); }, [user, load]);

  if (!user || user.role !== "admin") return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center" data-testid="admin-edu-denied">
      <p className="text-sm text-slate-600">Dostęp wyłącznie dla administratora (MFA).</p>
    </div>);
  if (!overview) return <div className="p-20 text-center text-slate-500" data-testid="admin-edu-loading">{err || "Ładowanie…"}</div>;

  const act = async (fn, okMsg) => {
    setErr(null); setMsg(null);
    try { const r = await fn(); setMsg(okMsg + (r?.data?.admin_invite_token
      ? ` — token administratora uczelni: /edu/zaproszenie/${r.data.admin_invite_token}` : "")); load(); }
    catch (ex) { setErr(formatApiError(ex)); }
  };

  const toggleDir = (inst) => act(() =>
    api.patch(`/admin/edu/institutions/${inst.id}`, { directory_opt_in: !inst.directory_opt_in }),
    "Zmieniono widoczność w katalogu.");

  const toggleExtended = (inst) => {
    const caps = new Set(inst.capabilities || []);
    caps.has("ACADEMIC_RESEARCH_EXTENDED") ? caps.delete("ACADEMIC_RESEARCH_EXTENDED")
      : caps.add("ACADEMIC_RESEARCH_EXTENDED");
    act(() => api.patch(`/admin/edu/institutions/${inst.id}`, { capabilities: [...caps] }),
      "Zmieniono uprawnienia badawcze.");
  };

  const editLimits = (inst) => {
    const v = window.prompt("Limity JSON, np. {\"lookups_per_day\": 40, \"exports_per_day\": 8, \"exports_per_month\": 50}:",
      JSON.stringify(inst.limits || {}));
    if (v === null) return;
    try { JSON.parse(v); } catch { setErr("Niepoprawny JSON"); return; }
    act(() => api.patch(`/admin/edu/institutions/${inst.id}`, { limits: JSON.parse(v) }), "Zapisano limity.");
  };

  const resolveReview = (rev) => {
    const resolution = window.prompt("Decyzja / uzasadnienie (audyt):");
    if (!resolution) return;
    const state = window.prompt("Stan konta po przeglądzie: ACTIVE | LIMITED | SUSPENDED", "ACTIVE");
    if (!["ACTIVE", "LIMITED", "SUSPENDED"].includes(state)) return;
    act(() => api.post(`/admin/edu/reviews/${rev.id}/resolve`,
      { member_state: state, resolution }), "Przegląd zamknięty.");
  };

  const decideException = (inv, decision) => {
    const note = window.prompt("Notatka do decyzji (odnotowywana w audycie):");
    if (!note) return;
    act(() => api.post(`/admin/edu/invite-exceptions/${inv.id}/decision`,
      { decision, note }),
      decision === "APPROVE" ? "Wyjątek zatwierdzony — zaproszenie aktywne." : "Wyjątek odrzucony.");
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="admin-edu-page">
      <Helmet><title>Administracja EDU — PlanGmina</title></Helmet>
      <h1 className="text-2xl font-bold text-slate-900">PlanGmina EDU — kontrola właściciela</h1>
      {err && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid="admin-edu-error">{err}</p>}
      {msg && <p className="mt-3 break-all rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="admin-edu-msg">{msg}</p>}

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="admin-edu-overview">
        {Object.entries(overview.institutions_by_status).map(([s, n]) => (
          <div key={s} className="rounded-xl border border-slate-300 p-3 text-center">
            <p className="text-xl font-bold">{n}</p><p className="text-[10px] uppercase text-slate-500">{STATUS_PL[s]}</p>
          </div>))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4 text-center text-xs text-slate-600" data-testid="admin-edu-totals">
        <p>Użytkownicy EDU: <b>{overview.edu_users}</b></p>
        <p>Raporty akademickie: <b>{overview.academic_reports}</b></p>
        <p>Eksporty: <b>{overview.academic_exports}</b></p>
        <p>Otwarte przeglądy: <b data-testid="admin-edu-open-reviews">{overview.open_reviews}</b></p>
      </div>

      <section className="mt-8" data-testid="admin-edu-institutions">
        <h2 className="text-lg font-bold text-slate-900">Uczelnie</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead><tr className="border-b border-slate-300 text-slate-500">
              <th className="py-2 pr-2">Uczelnia</th><th className="pr-2">Status</th><th className="pr-2">Domena</th>
              <th className="pr-2">Katalog</th><th className="pr-2">Extended</th><th>Akcje</th>
            </tr></thead>
            <tbody>
              {institutions.map((i) => (
                <tr key={i.id} className="border-b border-slate-100 align-top" data-testid={`admin-edu-inst-${i.id}`}>
                  <td className="py-2 pr-2"><b>{i.name}</b><br /><span className="text-slate-400">{i.city} · {i.application?.contact_email}</span></td>
                  <td className="pr-2">{STATUS_PL[i.status]}</td>
                  <td className="pr-2">{(i.domains || []).join(", ")}</td>
                  <td className="pr-2"><button onClick={() => toggleDir(i)} data-testid={`admin-edu-dir-${i.id}`} className="underline">{i.directory_opt_in ? "TAK" : "nie"}</button></td>
                  <td className="pr-2"><button onClick={() => toggleExtended(i)} data-testid={`admin-edu-ext-${i.id}`} className="underline">{(i.capabilities || []).includes("ACADEMIC_RESEARCH_EXTENDED") ? "TAK" : "nie"}</button></td>
                  <td className="space-x-2 whitespace-nowrap">
                    {i.status === "APPLIED" && <button onClick={() => act(() => api.post(`/admin/edu/institutions/${i.id}/verify`, {}), "Zweryfikowana.")} data-testid={`admin-edu-verify-${i.id}`} className="text-blue-700 underline">Weryfikuj</button>}
                    {i.status === "APPLIED" && <button onClick={() => { if (window.confirm(`Usunąć zgłoszenie „${i.name}"? Tej operacji nie można cofnąć.`)) act(() => api.delete(`/admin/edu/institutions/${i.id}`), "Zgłoszenie usunięte."); }} data-testid={`admin-edu-delete-${i.id}`} className="text-red-700 underline">Usuń</button>}
                    {i.status === "VERIFIED" && <button onClick={() => act(() => api.post(`/admin/edu/institutions/${i.id}/activate`), "Aktywowana.")} data-testid={`admin-edu-activate-${i.id}`} className="text-emerald-700 underline">Aktywuj</button>}
                    {i.status === "ACTIVE" && <button onClick={() => { const r = window.prompt("Powód zawieszenia:"); if (r) act(() => api.post(`/admin/edu/institutions/${i.id}/suspend`, { reason: r }), "Zawieszona."); }} data-testid={`admin-edu-suspend-${i.id}`} className="text-red-700 underline">Zawieś</button>}
                    {i.status === "SUSPENDED" && <button onClick={() => act(() => api.post(`/admin/edu/institutions/${i.id}/reactivate`), "Przywrócona.")} data-testid={`admin-edu-reactivate-${i.id}`} className="text-emerald-700 underline">Przywróć</button>}
                    <button onClick={() => editLimits(i)} data-testid={`admin-edu-limits-${i.id}`} className="text-slate-700 underline">Limity</button>
                  </td>
                </tr>))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-8" data-testid="admin-edu-exceptions">
        <h2 className="text-lg font-bold text-slate-900">Wyjątki domenowe zaproszeń</h2>
        <p className="mt-1 text-xs text-slate-500">Zaproszenia na adresy spoza domeny uczelni — aktywują się dopiero po Twoim zatwierdzeniu.</p>
        {exceptions.length === 0 && <p className="mt-2 text-sm text-slate-500">Brak oczekujących wyjątków.</p>}
        <ul className="mt-3 space-y-2">
          {exceptions.map((x) => (
            <li key={x.id} className="rounded-xl border border-slate-300 p-3 text-sm" data-testid={`admin-edu-exc-${x.id}`}>
              <p><b>{x.email}</b> — {x.institution_name} · {x.edu_role} · {x.created_at.slice(0, 16).replace("T", " ")}</p>
              <p className="mt-1 text-xs text-slate-600">Uzasadnienie uczelni: {x.exception_justification}</p>
              <div className="mt-2 space-x-2">
                <button onClick={() => decideException(x, "APPROVE")} data-testid={`admin-edu-exc-approve-${x.id}`}
                  className="rounded bg-emerald-700 px-3 py-1 text-xs font-semibold text-white">Zatwierdź</button>
                <button onClick={() => decideException(x, "DENY")} data-testid={`admin-edu-exc-deny-${x.id}`}
                  className="rounded bg-red-700 px-3 py-1 text-xs font-semibold text-white">Odrzuć</button>
              </div>
            </li>))}
        </ul>
      </section>

      <section className="mt-8" data-testid="admin-edu-reviews">
        <h2 className="text-lg font-bold text-slate-900">Kolejka przeglądów anty-nadużyciowych</h2>
        {reviews.length === 0 && <p className="mt-2 text-sm text-slate-500">Brak otwartych przeglądów.</p>}
        <ul className="mt-3 space-y-2">
          {reviews.map((r) => (
            <li key={r.id} className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm" data-testid={`admin-edu-review-${r.id}`}>
              <p><b>{r.reason}</b> — {r.email} · {r.institution_name} · {r.created_at.slice(0, 16).replace("T", " ")}</p>
              <p className="mt-1 text-xs text-slate-600">{JSON.stringify(r.meta)}</p>
              <button onClick={() => resolveReview(r)} data-testid={`admin-edu-resolve-${r.id}`}
                className="mt-2 rounded bg-slate-900 px-3 py-1 text-xs font-semibold text-white">Rozpatrz</button>
            </li>))}
        </ul>
      </section>
    </div>
  );
}
